import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionTemplate, useReducedMotion } from "framer-motion";
import { IMG } from "@/lib/content";

export function CleanReveal() {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const blur = useTransform(scrollYProgress, [0.05, 0.65], [14, 0]);
  const sat = useTransform(scrollYProgress, [0.05, 0.65], [0.45, 1]);
  const bright = useTransform(scrollYProgress, [0.05, 0.65], [0.72, 1.02]);
  const filter = useMotionTemplate`blur(${blur}px) saturate(${sat}) brightness(${bright})`;
  const inset = useTransform(scrollYProgress, [0.05, 0.75], [18, 0]);
  const clip = useMotionTemplate`inset(${inset}% ${inset}% ${inset}% ${inset}%)`;
  const scale = useTransform(scrollYProgress, [0.05, 0.75], [1.12, 1]);
  const lineW = useTransform(scrollYProgress, [0.2, 0.7], ["0%", "100%"]);
  const textOpacity = useTransform(scrollYProgress, [0.55, 0.75], [0, 1]);
  const textY = useTransform(scrollYProgress, [0.55, 0.75], [30, 0]);
  const hazeOpacity = useTransform(scrollYProgress, [0.05, 0.6], [0.55, 0]);
  const labelOpacity = useTransform(scrollYProgress, [0.0, 0.15, 0.5, 0.6], [1, 1, 1, 0]);

  return (
    <section ref={ref} data-testid="clean-reveal-container" className="relative bg-paper" style={{ height: reduced ? "auto" : "230vh" }} aria-label="Klarheit im Detail">
      <div className={`${reduced ? "relative" : "sticky top-0"} h-[100svh] overflow-hidden`}>
        <motion.div className="absolute inset-0" style={reduced ? undefined : { clipPath: clip }}>
          <motion.img
            src={IMG.reveal}
            alt="Architektonisches Interieur mit klaren Linien, Glas und Tageslicht"
            loading="lazy"
            decoding="async"
            sizes="100vw"
            className="w-full h-full object-cover"
            style={reduced ? undefined : { filter, scale }}
          />
          <motion.div className="absolute inset-0 bg-paper mix-blend-soft-light" style={reduced ? { opacity: 0 } : { opacity: hazeOpacity }} />
          <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-ink/80 via-ink/30 to-transparent" />
        </motion.div>

        <motion.div style={reduced ? undefined : { opacity: labelOpacity }} className="absolute top-28 left-0 right-0 container-x flex justify-between label text-ink/60 z-10">
          <span>Signature · Clean Reveal</span>
          <span className="hidden sm:block">Licht. Klarheit. Präzision.</span>
        </motion.div>

        <div className="absolute inset-x-0 bottom-0 container-x pb-16 md:pb-20 z-10">
          <motion.div className="h-px bg-paper mb-8 md:mb-10" style={reduced ? { width: "100%" } : { width: lineW }} />
          <motion.div style={reduced ? undefined : { opacity: textOpacity, y: textY }} className="flex flex-col md:flex-row md:items-end justify-between gap-6 text-paper">
            <h2 className="display text-5xl sm:text-6xl lg:text-7xl drop-shadow-sm">
              Detail<br />matters.
            </h2>
            <p className="max-w-sm text-sm md:text-base text-paper/85 leading-relaxed">
              Ein gepflegter Raum beginnt bei den Details. Kanten, Fugen, Glas, Griffe. Dort, wo man es erst merkt, wenn es fehlt.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
